/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { AccessStatus } from './useAccessStatus'

const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

export interface AdminUser {
  id: string
  email: string
  full_name: string | null
  avatar_url: string | null
  bio: string | null
  is_admin: boolean
  created_at: string
  updated_at: string
  project_count: number
  access_status?: AccessStatus
  pricing_tier?: {
    name: string
    display_name: string
  } | null
}

export interface PricingTier {
  id: string
  name: string
  display_name: string
  max_projects: number | null
  max_total_clips: number | null
  max_commits_per_project: number | null
  is_beta: boolean
  sort_order: number
}

/**
 * Fetch ALL users from the platform (admin only)
 * This uses the admin API endpoint which bypasses RLS
 */
export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Admin access required')
        }
        const errorText = await response.text()
        throw new Error(`Failed to fetch users: ${response.status} - ${errorText}`)
      }

      const data = await response.json()
      return data as AdminUser[]
    },
  })
}

/**
 * Toggle admin status for a user (admin only)
 */
export function useToggleAdminStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/admin/users/${userId}/admin-status?is_admin=${isAdmin}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to update admin status')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

/**
 * Fetch all pricing tiers (admin only)
 */
export function useAdminTiers() {
  return useQuery({
    queryKey: ['admin', 'tiers'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/admin/tiers`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to fetch tiers: ${response.status} - ${errorText}`)
      }

      return response.json() as Promise<PricingTier[]>
    },
  })
}

/**
 * Assign a pricing tier to a user (admin only)
 */
export function useAssignUserTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, tierName }: { userId: string; tierName: string }) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/api/admin/users/${userId}/tier`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier_name: tierName }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to assign tier')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}
